import {
  AppBar,
  Box,
  Container,
  GlobalStyles,
  IconButton,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { Logout } from '@mui/icons-material';
import { Outlet, useLocation, Link as RouterLink } from 'react-router-dom';
import Footer from './footer';
import { useUserContext } from '../lib/users-context';
import { AppContextProvider } from '../lib/app-context';

// Print-mode resets that strip the screen-only chrome padding the Layout
// adds around every page (Container's max-width + horizontal padding, and
// the inner content Box's `p: 4`). Without these, every print target
// inherits ~56px of leftover screen margin per side on top of @page,
// which crushes wide tables (Roster, Groups list) into a narrow band.
// Pages still set their own `@page` margin and hide whatever sub-page
// chrome they own (sub-tabs, headers, dialogs); this just fixes the
// shared layout leak. The body / html resets are belt-and-suspenders
// against UA print stylesheets that re-introduce a margin.
const layoutPrintStyles = (
  <GlobalStyles
    styles={{
      '@media print': {
        'html, body': {
          margin: '0 !important',
          padding: '0 !important',
        },
        '#root .MuiContainer-root': {
          maxWidth: 'none !important',
          padding: '0 !important',
          margin: '0 !important',
        },
        '#root .layout-content': {
          padding: '0 !important',
          margin: '0 !important',
        },
      },
    }}
  />
);

// Maps the current pathname onto a tab value. Early-returns rather than a
// ternary chain so the priority order (most-specific first) reads top-down.
const getTabValue = (pathname: string): string => {
  if (pathname.startsWith('/admin/activity-log')) return '/admin/activity-log';
  if (pathname.startsWith('/admin')) return '/admin';
  if (pathname.startsWith('/manage')) return '/manage';
  if (pathname.startsWith('/birthdays')) return '/birthdays';
  if (pathname.startsWith('/attendance')) return '/attendance';
  return '/';
};

// Typed wrapper around `<Tab component={RouterLink}>`. MUI's polymorphic
// `component` prop is generic in a way TS can't infer when used inline, so
// the usual workaround is `as any` — that's what Jacob flagged. Wrapping
// here once gives us a properly typed `<LinkTab to='/...' />`.
type LinkTabProps = {
  to: string;
  value: string;
  label: string;
};

const LinkTab = (props: LinkTabProps) => (
  <Tab component={RouterLink} {...props} />
);

const Layout = () => {
  const { isAdmin, user, logOut } = useUserContext();
  const pathname = useLocation().pathname;
  const tabValue = getTabValue(pathname);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {layoutPrintStyles}
      <AppBar position='static'>
        <Toolbar variant='dense' sx={{ gap: 2 }}>
          <Tabs
            value={tabValue}
            variant='scrollable'
            scrollButtons='auto'
            textColor='inherit'
            indicatorColor='secondary'
            sx={{ flex: 1 }}
          >
            {/*
              Each <Tab> must be a direct child of <Tabs> — MUI walks
              children via React.Children.map and Fragments are opaque to
              that traversal, so wrapping the admin tabs in <>...</> would
              break the value-driven selected-tab highlighting.
            */}
            <LinkTab to='/' value='/' label='Check-in' />
            <LinkTab to='/manage' value='/manage' label='Manage' />
            <LinkTab to='/birthdays' value='/birthdays' label='Birthdays' />
            <LinkTab to='/attendance' value='/attendance' label='Attendance' />
            {isAdmin && (
              <LinkTab to='/admin' value='/admin' label='Admin' />
            )}
            {isAdmin && (
              <LinkTab
                to='/admin/activity-log'
                value='/admin/activity-log'
                label='Activity log'
              />
            )}
          </Tabs>
          {user?.displayName && (
            <Typography
              variant='body2'
              sx={{ opacity: 0.85, whiteSpace: 'nowrap' }}
            >
              {user.displayName}
            </Typography>
          )}
          {user && (
            <Tooltip title='Log out'>
              <IconButton
                color='inherit'
                onClick={logOut}
                size='small'
                aria-label='Log out'
              >
                <Logout fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>
      </AppBar>
      <AppContextProvider>
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box className='layout-content' sx={{ p: 4, flex: 1 }}>
            <Outlet />
          </Box>
          <Footer />
        </Container>
      </AppContextProvider>
    </Box>
  );
};

export default Layout;
